import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["allowMultiSelection", "id", "label", "subtitle", "icon", "inlineIcon", "nestingLevel", "isSelected", "isIndeterminate", "isDisabled", "isExpandable", "isExpanded", "onSelect", "onToggleExpand"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useUniqueId from 'hooks/useUniqueId';
import Box from 'components/Box';
import Button from 'components/Button';
import { Checkbox } from 'components/CheckboxOption';
import Flex from 'components/Flex';
import OverflowTooltip from 'components/OverflowTooltip';
import { RadioButton } from 'components/RadioButtonOption';
import Text from 'components/Text';
import SelectionIndicator from 'components/ItemPicker/ListItem/SelectionIndicator';
import LabelBox from 'components/ItemPicker/ListItem/LabelBox';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function TreeViewItem(_ref) {
  var _ref$allowMultiSelect = _ref.allowMultiSelection,
    allowMultiSelection = _ref$allowMultiSelect === void 0 ? true : _ref$allowMultiSelect,
    id = _ref.id,
    label = _ref.label,
    subtitle = _ref.subtitle,
    icon = _ref.icon,
    inlineIcon = _ref.inlineIcon,
    _ref$nestingLevel = _ref.nestingLevel,
    nestingLevel = _ref$nestingLevel === void 0 ? 0 : _ref$nestingLevel,
    isSelected = _ref.isSelected,
    isIndeterminate = _ref.isIndeterminate,
    isDisabled = _ref.isDisabled,
    isExpandable = _ref.isExpandable,
    isExpanded = _ref.isExpanded,
    onSelect = _ref.onSelect,
    onToggleExpand = _ref.onToggleExpand,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var itemRef = useRef(null);
  var uniqueId = useUniqueId(id ? String(id) : undefined);
  var itemId = "list-item-".concat(uniqueId);
  var labelId = "list-item-label-".concat(uniqueId);
  var expandButtonId = "list-item-expand-".concat(uniqueId);
  var subtitleId = subtitle ? "list-item-subtitle-".concat(uniqueId) : undefined;
  var shouldAppearChecked = !isDisabled && (isSelected || isIndeterminate);
  var hasExpansionButton = isExpandable !== undefined;
  function handleRowClick(e) {
    // We're handling changes on the wrapper element to make the whole
    // item picker row clickable.
    // Originally we were using a pseudo element inside of the <label>
    // that covered the row area, but this prevented using other
    // pointer events within the row, e.g. for tooltips
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled) {
      var _itemRef$current;
      // We'll return the item and the new state the checkbox should be in.
      // Indeterminate checkboxes should always return to a checked state first.
      onSelect(isIndeterminate || !isSelected);
      (_itemRef$current = itemRef.current) === null || _itemRef$current === void 0 || _itemRef$current.focus();
    }
  }
  function toggleExpand(e) {
    e.preventDefault();
    e.stopPropagation();
    onToggleExpand === null || onToggleExpand === void 0 || onToggleExpand(!isExpanded);
  }
  return /*#__PURE__*/_jsxs(Flex, _objectSpread(_objectSpread({
    position: "relative",
    height: 48,
    borderBottom: "thin"
  }, otherProps), {}, {
    p: "xxs",
    pl: 32 * nestingLevel,
    onClick: handleRowClick,
    children: [shouldAppearChecked && /*#__PURE__*/_jsx(SelectionIndicator, {}), hasExpansionButton && /*#__PURE__*/_jsx(Box, {
      ml: "xs",
      mr: "-m",
      width: 42,
      children: isExpandable && /*#__PURE__*/_jsx(Button, {
        iconOnly: true,
        variant: "transparent",
        id: expandButtonId,
        icon: isExpanded ? 'nav-down' : 'nav-right',
        onClick: toggleExpand,
        "aria-expanded": isExpanded,
        "aria-labelledby": "".concat(expandButtonId, " ").concat(labelId),
        children: t('actions_open')
      })
    }), /*#__PURE__*/_jsx(Box, {
      position: "relative",
      display: "flex",
      z: "raised",
      ml: "l",
      mr: "m",
      children: allowMultiSelection ? /*#__PURE__*/_jsx(Checkbox, {
        id: itemId,
        ref: itemRef,
        checked: isSelected,
        disabled: isDisabled,
        indeterminate: isIndeterminate,
        "aria-describedby": subtitleId,
        onChange: function onChange(e) {
          return e.preventDefault();
        }
      }) : /*#__PURE__*/_jsx(RadioButton, {
        id: itemId,
        value: id,
        ref: itemRef,
        checked: isSelected,
        disabled: isDisabled,
        "aria-describedby": subtitleId,
        onChange: function onChange(e) {
          return e.preventDefault();
        }
      })
    }), icon && /*#__PURE__*/_jsx(Box, {
      mr: "s",
      style: isDisabled ? {
        opacity: 0.5
      } : {
        opacity: 1
      },
      children: icon
    }), inlineIcon && /*#__PURE__*/_jsx(Box, {
      mr: "xxs",
      style: isDisabled ? {
        opacity: 0.5
      } : {
        opacity: 1
      },
      children: inlineIcon
    }), /*#__PURE__*/_jsxs(Box, {
      grow: true,
      shrink: true,
      dimmed: isDisabled,
      basis: 0,
      lineHeight: 1.3,
      display: "flex",
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(OverflowTooltip, {
        content: label,
        children: /*#__PURE__*/_jsx(LabelBox, {
          as: "label",
          display: "block",
          overflow: "ellipsis",
          position: "relative",
          htmlFor: itemId,
          id: labelId,
          isChecked: shouldAppearChecked,
          children: label
        })
      }), subtitle && /*#__PURE__*/_jsx(Text, {
        dimmed: true,
        size: "xs",
        overflow: "ellipsis",
        id: subtitleId,
        children: subtitle
      })]
    })]
  }));
}
export default TreeViewItem;